import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "trend__axis-y" }
const _hoisted_2 = { class: "trend__plot" }
const _hoisted_3 = ["viewBox", "aria-label"]
const _hoisted_4 = ["stop-color"]
const _hoisted_5 = ["stop-color"]
const _hoisted_6 = ["x1", "x2", "y1", "y2"]
const _hoisted_7 = ["d", "fill"]
const _hoisted_8 = ["d", "stroke"]
const _hoisted_9 = ["x1", "x2", "y1", "y2"]
const _hoisted_10 = ["cx", "cy", "stroke"]
const _hoisted_11 = { class: "trend__axis-x" }

import { computed, ref } from 'vue'

export interface TrendPoint {
  label: string
  value: number
}

const W = 720
const H = 240

export default /*@__PURE__*/_defineComponent({
  __name: 'SellerTrendChart',
  props: {
    points: {},
    color: { default: '#1a5fa8' },
    unit: { default: '원' },
    ariaLabel: { default: '기간별 추이 그래프' }
  },
  setup(__props: any) {

const props = __props

/* viewBox 좌표계. 실제 크기는 CSS가 정한다. */
const PAD = { t: 16, r: 8, b: 26, l: 8 }

const svgRef = ref<SVGSVGElement | null>(null)
const hover = ref<{ x: number; y: number; label: string; value: number } | null>(null)

const gradientId = `trend-grad-${Math.random().toString(36).slice(2, 8)}`

const maxValue = computed(() => {
  const max = Math.max(...props.points.map((p) => p.value), 0)
  if (max <= 0) return 1
  /* 눈금이 어중간하게 끊기지 않도록 위쪽을 올림한다 */
  const digits = Math.pow(10, Math.floor(Math.log10(max)))
  return Math.ceil((max * 1.1) / digits) * digits
})

const coords = computed(() => {
  const count = props.points.length
  if (!count) return []
  const innerW = W - PAD.l - PAD.r
  const innerH = H - PAD.t - PAD.b

  return props.points.map((point, index) => ({
    ...point,
    x: PAD.l + (count === 1 ? innerW / 2 : (innerW * index) / (count - 1)),
    y: PAD.t + innerH - (point.value / maxValue.value) * innerH,
  }))
})

const linePath = computed(() => {
  const list = coords.value
  if (list.length < 2) return ''
  /* 완만한 곡선 — 인접 점의 중간을 제어점으로 삼는 Catmull-Rom 근사 */
  return list.reduce((path, point, index) => {
    if (index === 0) return `M ${point.x} ${point.y}`
    const prev = list[index - 1]
    const cx = (prev.x + point.x) / 2
    return `${path} C ${cx} ${prev.y}, ${cx} ${point.y}, ${point.x} ${point.y}`
  }, '')
})

const areaPath = computed(() => {
  if (!linePath.value) return ''
  const list = coords.value
  const first = list[0]
  const last = list[list.length - 1]
  return `${linePath.value} L ${last.x} ${H - PAD.b} L ${first.x} ${H - PAD.b} Z`
})

const gridLines = computed(() => {
  const steps = 4
  const innerH = H - PAD.t - PAD.b
  return Array.from({ length: steps + 1 }, (_, index) => ({
    y: PAD.t + (innerH * index) / steps,
    value: (maxValue.value * (steps - index)) / steps,
  }))
})

const xTicks = computed(() => {
  const list = coords.value
  if (!list.length) return []
  const wanted = Math.min(6, list.length)
  const step = Math.max(1, Math.floor((list.length - 1) / (wanted - 1 || 1)))
  const ticks: Array<{ x: number; label: string }> = []
  for (let index = 0; index < list.length; index += step) {
    ticks.push({ x: list[index].x, label: list[index].label })
  }
  const last = list[list.length - 1]
  if (ticks[ticks.length - 1]?.label !== last.label) {
    ticks.push({ x: last.x, label: last.label })
  }
  return ticks
})

function onMove(event: MouseEvent) {
  const list = coords.value
  if (!list.length || !svgRef.value) return
  const rect = svgRef.value.getBoundingClientRect()
  const ratio = (event.clientX - rect.left) / rect.width
  const index = Math.round(ratio * (list.length - 1))
  const point = list[Math.min(Math.max(index, 0), list.length - 1)]
  hover.value = { x: point.x, y: point.y, label: point.label, value: point.value }
}

function shortNumber(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}억`
  if (value >= 10000) return `${Math.round(value / 10000)}만`
  return Math.round(value).toLocaleString('ko-KR')
}

function format(value: number): string {
  return `${value.toLocaleString('ko-KR')}${props.unit}`
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("figure", {
    class: "trend",
    onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hover.value = null))
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gridLines.value, (line, index) => {
        return (_openBlock(), _createElementBlock("span", {
          key: `ty-${index}`,
          style: _normalizeStyle({ top: `${(line.y / H) * 100}%` })
        }, _toDisplayString(shortNumber(line.value)), 5))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(), _createElementBlock("svg", {
        ref_key: "svgRef",
        ref: svgRef,
        class: "trend__svg",
        viewBox: `0 0 ${W} ${H}`,
        preserveAspectRatio: "none",
        role: "img",
        "aria-label": __props.ariaLabel,
        onMousemove: onMove
      }, [
        _createElementVNode("defs", null, [
          _createElementVNode("linearGradient", {
            id: gradientId,
            x1: "0",
            y1: "0",
            x2: "0",
            y2: "1"
          }, [
            _createElementVNode("stop", {
              offset: "0%",
              "stop-color": __props.color,
              "stop-opacity": "0.26"
            }, null, 8, _hoisted_4),
            _createElementVNode("stop", {
              offset: "100%",
              "stop-color": __props.color,
              "stop-opacity": "0"
            }, null, 8, _hoisted_5)
          ])
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gridLines.value, (line, index) => {
          return (_openBlock(), _createElementBlock("line", {
            key: `grid-${index}`,
            class: "trend__grid",
            x1: PAD.l,
            x2: W - PAD.r,
            y1: line.y,
            y2: line.y
          }, null, 8, _hoisted_6))
        }), 128)),
        (areaPath.value)
          ? (_openBlock(), _createElementBlock("path", {
              key: 0,
              class: "trend__area",
              d: areaPath.value,
              fill: `url(#${gradientId})`
            }, null, 8, _hoisted_7))
          : _createCommentVNode("", true),
        (linePath.value)
          ? (_openBlock(), _createElementBlock("path", {
              key: 1,
              class: "trend__line",
              d: linePath.value,
              stroke: __props.color
            }, null, 8, _hoisted_8))
          : _createCommentVNode("", true),
        (hover.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("line", {
                class: "trend__cursor",
                x1: hover.value.x,
                x2: hover.value.x,
                y1: PAD.t,
                y2: H - PAD.b
              }, null, 8, _hoisted_9),
              _createElementVNode("circle", {
                class: "trend__dot",
                cx: hover.value.x,
                cy: hover.value.y,
                r: 4.5,
                stroke: __props.color
              }, null, 8, _hoisted_10)
            ], 64))
          : _createCommentVNode("", true)
      ], 40, _hoisted_3)),
      _createElementVNode("div", _hoisted_11, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(xTicks.value, (tick) => {
          return (_openBlock(), _createElementBlock("span", {
            key: tick.label,
            style: _normalizeStyle({ left: `${(tick.x / W) * 100}%` })
          }, _toDisplayString(tick.label), 5))
        }), 128))
      ]),
      (hover.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "trend__tip",
            style: _normalizeStyle({ left: `${(hover.value.x / W) * 100}%`, top: `${(hover.value.y / H) * 100}%` })
          }, [
            _createElementVNode("strong", null, _toDisplayString(hover.value.label), 1),
            _createElementVNode("span", null, _toDisplayString(format(hover.value.value)), 1)
          ], 4))
        : _createCommentVNode("", true)
    ])
  ], 32))
}
}

})